import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout, { Breadcrumb } from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { appCategories, comicApps } from "@/mocks/apps";
import Rating from "@/components/base/Rating";
import { articles } from "@/mocks/articles";

const medal = ["bg-secondary-500", "bg-background-400", "bg-primary-500"];

export default function Rankings() {
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") ?? "全部";

  const list = useMemo(
    () => (active === "全部" ? comicApps : comicApps.filter((a) => a.category === active)),
    [active]
  );

  usePageMeta({
    title: "漫画软件应用排行榜 - 漫画软件应用哪个好",
    description:
      "查看漫画软件应用排行，按免费追更、正版日漫、国漫原创、韩漫专区等分类对比评分与口碑，快速锁定好用的漫画软件应用。",
    canonical: "https://mangao0.github.io/rankings",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "漫画软件应用排行榜",
      description: "漫画软件应用排行与分类榜单",
      url: "https://mangao0.github.io/rankings",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: comicApps.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${a.name}漫画软件应用`,
        })),
      },
    },
  });

  const switchCat = (c: string) => {
    setParams(c === "全部" ? {} : { cat: c });
  };

  return (
    <Layout title="排行榜">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "漫画软件应用排行榜" }]} />

      <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-4 md:px-6">
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">漫画软件应用排行榜</h1>
          <p className="text-sm text-foreground-600">漫画软件应用哪个好？结合评分、资源、更新与口碑，为你呈现这份实用榜单。</p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 rounded-full border border-background-200 bg-background-100 p-1">
          {appCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => switchCat(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c ? "bg-primary-500 font-bold text-background-50" : "text-foreground-700 hover:bg-background-200/70"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_300px]">
          <div className="space-y-4">
            {list.map((app, i) => (
              <article key={app.id} className="flex flex-col gap-4 overflow-hidden rounded-2xl border border-background-200/80 bg-background-100 p-4 transition-colors hover:border-primary-300 sm:flex-row md:p-5">
                <div className="flex items-start gap-3 sm:flex-col sm:items-center sm:gap-2">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg font-black text-background-50 ${medal[i] ?? "bg-background-400"}`}>
                    {i + 1}
                  </span>
                  <div className="hidden text-xs text-foreground-500 sm:block">综合评分</div>
                  <span className="hidden items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2 py-0.5 text-sm font-black text-background-50 sm:flex">
                    {app.score}
                  </span>
                </div>

                <Link to={`/reviews/${app.id}`} className="mx-auto h-28 w-20 shrink-0 overflow-hidden rounded-lg bg-background-200 sm:mx-0 sm:h-36 sm:w-[104px]">
                  <img
                    src={app.cover}
                    alt={`${app.name}漫画软件应用封面`}
                    title={`${app.name}漫画软件应用`}
                    className="h-full w-full object-cover"
                  />
                </Link>

                <div className="flex flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold text-foreground-950">
                      <Link to={`/reviews/${app.id}`} className="transition-colors hover:text-primary-600">{app.name}</Link>
                    </h2>
                    <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{app.category}</span>
                    <span className="whitespace-nowrap rounded-md bg-background-200 px-2 py-0.5 text-xs text-foreground-600">{app.price}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-foreground-600">{app.tagline}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Rating value={app.rating} />
                    <span className="text-xs text-foreground-500">{app.rating} 分</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-700">{app.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {app.tags.map((t) => (
                      <span key={t} className="whitespace-nowrap rounded-md bg-background-200 px-2 py-0.5 text-xs text-foreground-600">{t}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <p className="hidden text-xs text-foreground-600 sm:block">适合：{app.bestFor}</p>
                    <Link
                      to={`/reviews/${app.id}`}
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      查看评测
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-2xl border border-background-200/80 bg-background-100 p-5">
              <h3 className="text-base font-bold text-foreground-950">榜单说明</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                榜单基于漫画软件应用的阅读体验、资源数量、更新速度、付费门槛与用户口碑综合评定，供你参考决策。没有绝对最好，只有最适合。
              </p>
              <Link to="/reviews/beginners-guide" className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-bold text-primary-600 transition-colors hover:text-primary-700">
                查看新手选型指南
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-2xl border border-background-200/80 bg-background-100 p-5">
              <h3 className="text-base font-bold text-foreground-950">相关评测</h3>
              <ul className="mt-3 space-y-3">
                {articles.slice(0, 3).map((a) => (
                  <li key={a.id}>
                    <Link to={`/reviews/${a.id}`} className="line-clamp-2 text-sm leading-relaxed text-foreground-700 transition-colors hover:text-primary-600">
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}